import { ToolExecutionState } from 'ducks/chatDrawer'
import { Dispatch } from 'redux'
import { findTargetMessageIndex, getLastMessageIndex } from '../helpers'

export interface UseToolExecutionStateOptions {
  toolType: string
  projectId?: number
  isAutoAccepted?: boolean
  initialContent?: string
}

export interface ToolExecutionStateHelpers {
  setRunning: (description?: string) => void
  setReady: (description: string, data?: any) => void
  setCompleted: (description: string) => void
  setError: (description: string, error?: any) => void
  clear: () => void
}

export function createToolExecutionStateHelpers(
  dispatch: Dispatch,
  options: UseToolExecutionStateOptions
): ToolExecutionStateHelpers {
  const { toolType, projectId, isAutoAccepted = false, initialContent } = options

  const updateToolExecutionState = (
    status: ToolExecutionState['status'],
    description?: string,
    data?: any,
    error?: any
  ) => {
    const targetMessageIndex = findTargetMessageIndex(toolType)

    if (targetMessageIndex >= 0) {
      dispatch({
        type: 'UPDATE_TOOL_EXECUTION_STATE',
        messageIndex: targetMessageIndex,
        toolExecutionState: {
          toolType,
          status,
          description,
          data,
          error,
          projectId,
          isAutoAccepted,
        },
      })
    }
  }

  const setRunning = (description?: string) => {
    const lastMessageIndex = getLastMessageIndex()

    if (lastMessageIndex >= 0) {
      dispatch({
        type: 'UPDATE_TOOL_EXECUTION_STATE',
        messageIndex: lastMessageIndex,
        toolExecutionState: {
          toolType,
          status: 'running',
          description,
          projectId,
          isAutoAccepted,
        },
      })
    } else {
      // Fallback: create a new message if there are no messages
      dispatch({
        type: 'ADD_MESSAGE',
        content: initialContent || 'Processing...',
        direction: 'receive',
        origin: 'app',
        sharedWithContexts: ['text'],
        toolExecutionState: {
          toolType,
          status: 'running',
          description,
          projectId,
          isAutoAccepted,
        },
      })
    }
  }

  const setReady = (description: string, data?: any) => {
    updateToolExecutionState('ready', description, data)
  }

  const setCompleted = (description: string) => {
    updateToolExecutionState('completed', description)
  }

  const setError = (description: string, error?: any) => {
    updateToolExecutionState('error', description, undefined, error)
  }

  const clear = () => {
    const targetMessageIndex = findTargetMessageIndex()
    if (targetMessageIndex >= 0) {
      dispatch({
        type: 'UPDATE_TOOL_EXECUTION_STATE',
        messageIndex: targetMessageIndex,
        toolExecutionState: undefined as any,
      })
    }
  }

  return {
    setRunning,
    setReady,
    setCompleted,
    setError,
    clear,
  }
}
